import React from 'react';

import {accessGroupEnum, shareItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import {MembersRequester} from '../../Domain/Groups/MembersRequester';
import {GroupsRequester} from '../../Domain/Groups/GroupsRequester';

import {GroupRow} from '../../Domain/Groups/helpers/GroupRow';
import {MemberRow} from '../../Domain/Groups/helpers/MemberRow';

const SectionHeader = ({label}) => (
    <div className="px-2 pt-3 pb-1 text-xs font-semibold text-main-hover uppercase tracking-wide">{label}</div>
);

const renderShareItem = (item, isChecked, onToggle) => {
    if (item._sectionHeader) return <SectionHeader key={item.value} label={item.label} />;
    if (item._type === shareItemTypeEnum.GROUP) return <GroupRow key={item.value} group={item} checked={isChecked} onToggle={onToggle} />;
    return <MemberRow key={item.value} user={item} checked={isChecked} onToggle={onToggle} />;
};

const toGroupItem = (g) => ({...g, value: g.GroupId, label: g.GroupName, _type: shareItemTypeEnum.GROUP});

const toMemberItem = (m) => ({...m, value: m._id, label: [m.FirstName, m.LastName].filter(Boolean).join(' ') || m.Email, _type: shareItemTypeEnum.MEMBER});

const isSpecialAccessSource = (s) => s === accessGroupEnum.WORKSPACE_PUBLIC || s === accessGroupEnum.GUEST;

const insertSectionHeaders = (groups, members) => {
    const items = [];
    if (groups.length) {
        items.push({_sectionHeader: true, value: '__groups_header', label: 'GROUPS'});
        items.push(...groups);
    }
    if (members.length) {
        items.push({_sectionHeader: true, value: '__members_header', label: 'MEMBERS'});
        items.push(...members);
    }
    return items;
};

const fetchShareData = async (workspace, projectId, searchStr, currentAccessSet) => {
    const result = await MembersRequester.getGroupsAndMembersForSelect(workspace, {searchStr, projectId, excludeAdminsAndOwners: true});
    const groups = (result?.groups?.data || []).map(toGroupItem);
    const members = (result?.members?.data || []).map(toMemberItem);
    const data = insertSectionHeaders(groups, members);
    return {data, totalCount: data.length, initialSelected: [...currentAccessSet]};
};

const splitSelectionByType = (fetchedItems, selectedIds) => {
    const selectedSet = new Set(selectedIds);
    const selectedGroups = fetchedItems.filter((i) => i._type === shareItemTypeEnum.GROUP && selectedSet.has(i.value));
    const selectedMembers = fetchedItems.filter((i) => i._type === shareItemTypeEnum.MEMBER && selectedSet.has(i.value));
    return {selectedGroups, selectedMembers};
};

const SHARE_TITLES = {
    item: 'Share Item With',
    folder: 'Share Folder With',
    project: 'Share Project With',
};

const buildShareModalConfig = ({workspace, projectId, currentAccessSet, addRef, onSave, itemType}) => {
    let fetchedItems = [];
    return {
        anchorEl: addRef.current,
        title: SHARE_TITLES[itemType] || SHARE_TITLES.item,
        searchPlaceHolder: 'Search members and groups...',
        isMultipleChoices: true,
        showActionBar: true,
        showSelectedCount: true,
        fetchData: async (skip, limit, searchStr) => {
            const result = await fetchShareData(workspace, projectId, searchStr, currentAccessSet);
            fetchedItems = result.data.filter((item) => !item._sectionHeader);
            return result;
        },
        renderItem: renderShareItem,
        onSave: (selectedIds) => {
            const {selectedGroups, selectedMembers} = splitSelectionByType(fetchedItems, selectedIds);
            onSave(selectedIds, selectedGroups, selectedMembers);
        },
    };
};

export const fetchItemAccessSources = (workspace, {itemId, nestedRoute}) =>
    GroupsRequester.getItemAccessSources(workspace, {itemId, nestedRoute});

export const openShareItemModal = ({workspace, projectId, currentAccessSources, modalStore, addRef, onSave, itemType}) => {
    const currentAccessSet = new Set(currentAccessSources.filter((s) => !isSpecialAccessSource(s)));
    modalStore.modalData.genericSelectData = buildShareModalConfig({workspace, projectId, currentAccessSet, addRef, onSave, itemType});
    modalStore.isGenericSelectSearchModalOpen = true;
};
